/* Native Shopify cart/search/facets remain in charge of all commerce requests. */
if (!customElements.get('ali-carousel')) {
  customElements.define('ali-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-ali-track]');
      this.slides = [...this.querySelectorAll('[data-ali-slide]')];
      this.dots = [...this.querySelectorAll('[data-ali-dot]')];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.motion.matches;
      this.visible = true;
      if (!this.track || !this.slides.length) return;
      this.positionArrows();
      this.setAttribute('data-ready', '');
      this.addEventListener('click', event => {
        const control = event.target.closest('button');
        if (!control) return;
        if (control.hasAttribute('data-ali-next')) this.go(this.index + 1);
        if (control.hasAttribute('data-ali-prev')) this.go(this.index - 1);
        if (control.hasAttribute('data-ali-dot')) this.go(Number(control.dataset.aliDot));
        if (control.hasAttribute('data-ali-pause')) {
          this.paused = !this.paused;
          this.updatePauseButton();
          this.schedule();
        }
      }, { signal });
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => { this.update(); this.schedule(); }, 120);
      }, { passive: true, signal });
      // Touch uses native horizontal scrolling, retaining vertical page scroll and zoom.
      // Mouse dragging is added without preventing a normal click on the banner link.
      this.addEventListener('pointerdown', event => {
        this.pointerActive = true;
        if (!event.target.closest('.ali-hero__image-link')) return;
        this.interacting = true;
        this.schedule();
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        const left = this.track.scrollLeft;
        this.track.scrollTo({ left, behavior: 'instant' });
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left, moved: false };
      }, { signal });
      window.addEventListener('pointermove', event => this.movePointer(event), { signal });
      ['pointerup', 'pointercancel'].forEach(type => window.addEventListener(type, event => this.endPointer(event), { signal }));
      this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.suppressClickUntil = 0;
        }
      }, { capture: true, signal });
      // Keyboard focus stops rotation until the customer explicitly resumes it.
      this.addEventListener('focusin', () => {
        if (this.pointerActive) return;
        this.paused = true; this.updatePauseButton(); this.schedule();
      }, { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, { signal });
      this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); } }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updatePauseButton(); this.schedule();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (selected >= 0) { this.editorSelected = true; this.go(selected); }
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, { signal });
      this.resizeObserver = new ResizeObserver(() => {
        if (this.width !== this.track.clientWidth) {
          this.width = this.track.clientWidth;
          this.track.scrollTo({ left: this.index * this.width, behavior: 'instant' });
        }
        this.positionArrows();
      });
      this.resizeObserver.observe(this.track);
      this.querySelectorAll('.ali-hero__image-link').forEach(image => this.resizeObserver.observe(image));
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0 });
        this.visibilityObserver.observe(this);
      }
      this.update(); this.updatePauseButton(); this.schedule();
    }
    positionArrows() {
      const image = this.slides[this.index]?.querySelector('.ali-hero__image-link');
      if (image) this.style.setProperty('--ali-hero-image-height', `${image.getBoundingClientRect().height}px`);
    }
    movePointer(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(dx) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
        drag.moved = true;
        this.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      this.track.scrollLeft = drag.left - dx;
    }
    endPointer(event) {
      this.pointerActive = false;
      this.interacting = false;
      const drag = this.drag;
      if (drag && event.pointerId === drag.id) {
        this.drag = null;
        this.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        if (drag.moved) {
          this.suppressClickUntil = performance.now() + 400;
          const distance = drag.x - event.clientX;
          const start = Math.round(drag.left / this.track.clientWidth);
          const target = Math.abs(distance) > Math.min(80, this.track.clientWidth * .15)
            ? start + Math.sign(distance) : start;
          this.go(Math.max(0, Math.min(this.slides.length - 1, target)));
        }
      }
      this.schedule();
    }
    go(index) {
      const wrapping = index < 0 || index >= this.slides.length;
      this.index = (index + this.slides.length) % this.slides.length;
      // Make the destination available before scrolling it into view.
      this.slides[this.index].inert = false;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: this.motion.matches || wrapping ? 'instant' : 'smooth' });
      this.positionArrows();
      this.schedule();
    }
    update() {
      if (!this.track.clientWidth) return;
      this.index = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / this.track.clientWidth)));
      this.dots.forEach((dot, i) => i === this.index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
      this.slides.forEach((slide, i) => { slide.inert = i !== this.index; });
      this.positionArrows();
    }
    updatePauseButton() {
      const button = this.querySelector('[data-ali-pause]');
      if (!button) return;
      button.setAttribute('aria-pressed', String(this.paused));
      button.setAttribute('aria-label', this.paused ? 'Reanudar diapositivas' : 'Pausar diapositivas');
      button.textContent = this.paused ? '▶' : 'Ⅱ';
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.interacting || this.editorSelected || !this.visible || document.hidden || this.slides.length < 2) return;
      this.timer = setTimeout(() => { this.go(this.index + 1); }, Math.max(5000, Number(this.dataset.interval) || 5000));
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.timer); clearTimeout(this.scrollTimer);
    }
  });
}

// Sense deliberately uses novalidate on AJAX forms. Validate the extra card
// quantity before its submit handler runs; inventory remains server-authoritative.
if (!window.aliCardQuantityValidation) {
  window.aliCardQuantityValidation = true;
  document.addEventListener('submit', event => {
    const input = event.target.querySelector?.('[data-ali-card-quantity] input[name="quantity"]');
    if (!input) return;
    const quantity = Number(input.value);
    input.setCustomValidity(Number.isSafeInteger(quantity) && quantity >= 1 ? '' : 'Elige una cantidad entera de 1 o más.');
    if (!input.checkValidity()) {
      event.preventDefault(); event.stopImmediatePropagation(); input.reportValidity();
    }
  }, true);
  ['input', 'change'].forEach(type => document.addEventListener(type, event => {
    if (!event.target.matches('[data-ali-card-quantity] input')) return;
    event.target.setCustomValidity('');
    const form = event.target.closest('product-form');
    const button = form?.querySelector('[type="submit"]');
    if (form?.error && button && !button.disabled && !button.classList.contains('loading')) {
      form.error = false;
      form.handleErrorMessage?.();
      button.removeAttribute('aria-disabled');
      button.querySelector('span')?.classList.remove('hidden');
      button.querySelector('.sold-out-message')?.classList.add('hidden');
    }
  }));
}
